import ImageUpload from '@/components/ImageUpload';
import { apiCheckCoCode, apiOcr } from '@/network/api/api';
import { ProFormDateRangePicker } from '@ant-design/pro-components';
import $businessIcon from '@assets/form/business-icon.png';
import Province from '@components/Province';
import type { FormInstance } from 'antd';
import { Form, Image, Input, Modal, Radio, Space, message } from 'antd';
import FormItem from 'antd/es/form/FormItem';
import dayjs from 'dayjs';
import { useState } from 'react';
import { ExpirationDateTypeEnum } from '../constants';

const IMG_URL = import.meta.env.VITE_IMAGE_URL;

interface CompanyInfoFormProps {
  formRef: FormInstance;
  formData: React.MutableRefObject<any[]>;
  currentStep: number;
  onFinish: (title?: string) => void;
  require: boolean;
  setRequire: (v: boolean) => void;
  setCanGoLogin: (v: boolean) => void;
}

const CompanyInfoForm: React.FC<CompanyInfoFormProps> = ({
  formRef,
  formData,
  currentStep,
  onFinish,
  require,
  setRequire,
  setCanGoLogin,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [modal, contextHolderModal] = Modal.useModal();
  const [status, setStatus] = useState(true);

  return (
    <>
      {contextHolder}
      {contextHolderModal}
      <Form layout="vertical" form={formRef} size="large" onFinish={() => onFinish('企业信息')}>
        <FormItem
          label={
            <>
              <span className="tip">*</span>营业执照上传
            </>
          }
        >
          <Space size={100} align="start">
            <FormItem name="businessLicenseImageUrl" rules={[{ required: true, message: '请上传营业执照' }]} noStyle>
              <ImageUpload
                maxCount={1}
                defaultValue={formRef.getFieldValue('businessLicenseImageUrl')}
                size={5}
                setStatus={() => {
                  setStatus(true);
                }}
                onChange={(e) => {
                  if (e && status) {
                    setStatus(false);
                    messageApi.open({
                      type: 'loading',
                      content: '识别中...',
                      duration: 0,
                    });
                    // 图片识别
                    apiOcr(encodeURIComponent(IMG_URL + e))
                      .then(async (res: any) => {
                        if (res.code == 20000 && res.data?.business_license) {
                          const oldData = formRef.getFieldsValue();
                          const data = res.data.business_license;
                          const obj: any = {};
                          const objStay: any = {};
                          const keys: any = {
                            name: 'coName',
                            credit_code: 'coCode',
                            address: 'coAddressInfo',
                            dateType: 'licenseExpirationDateType',
                          };
                          // 构造数据
                          data.dateType = oldData.licenseExpirationDateType;
                          data.date = oldData.businessLicenseDate;
                          const sum = data.business_term || [];
                          const [m, n] = [dayjs(sum[0]), dayjs(sum[1])];
                          if (sum.length === 2 && m.isValid() && n.isValid()) {
                            data.dateType = ExpirationDateTypeEnum[1].value;
                            data.date = [m, n];
                          } else if (JSON.stringify(sum).includes('长期')) {
                            data.dateType = ExpirationDateTypeEnum[0].value;
                            data.date = [];
                          }
                          // 日期单独处理
                          if (data.business_term) {
                            if (
                              oldData.businessLicenseDate?.length &&
                              !(
                                oldData.businessLicenseDate[0].isSame(data.date[0], 'day') &&
                                oldData.businessLicenseDate[1].isSame(data.date[1], 'day')
                              )
                            ) {
                              objStay.businessLicenseDate = data.date;
                            } else {
                              obj.businessLicenseDate = data.date;
                            }
                          }
                          // 其它数据处理
                          Object.keys(keys).forEach((key) => {
                            const field = keys[key];
                            if (data[key]) {
                              if (oldData[field] && oldData[field] !== data[key]) {
                                objStay[field] = data[key];
                              } else {
                                obj[field] = data[key];
                              }
                            }
                          });
                          // 数据覆盖提示
                          let b = false;
                          if (Object.keys(objStay).length) {
                            b = await modal.confirm({
                              content: '已有已输入信息，是否覆盖？',
                              okText: '确定',
                              cancelText: '取消',
                            });
                          } else {
                            message.success('识别成功，已自动填充');
                          }
                          formRef.setFieldsValue({ ...obj, ...(b ? objStay : {}) });
                          setRequire(
                            formRef.getFieldValue('licenseExpirationDateType') == ExpirationDateTypeEnum[1].value,
                          );
                        } else {
                          message.info('识别失败，请重新上传');
                        }
                      })
                      .finally(() => {
                        messageApi.destroy();
                      });
                  }
                }}
              />
            </FormItem>
            <Space direction="vertical" align="center">
              <Image
                src={$businessIcon}
                style={{ width: 148, height: 102, padding: 4, border: '1px solid rgba(0, 0, 0, .15)', borderRadius: 8 }}
                preview={false}
              />
              示例图
            </Space>
          </Space>
          <div style={{ color: '#979797', fontSize: 12, marginTop: 10 }}>
            请按照指引上传对应的图片：
            <br />
            1、请上传主体的证件彩色照片，证照需要正面拍摄，露出证件四角且清晰、完整，所有字符清晰可识别，不得反光或遮挡，不得翻拍、截图、镜像、PS。
            <br />
            2、图片只支持 JPG JPEG PNG 格式，文件大小建议 1M，不能超过 5M。
            <br />
            3、营业执照支持自动识别。
          </div>
        </FormItem>
        <FormItem label="企业名称" name="coName" rules={[{ required: true }, { max: 50 }]}>
          <Input placeholder="请输入" />
        </FormItem>
        <FormItem
          label="统一社会信用代码"
          name="coCode"
          validateTrigger="onBlur"
          rules={[
            { required: true, len: 18 },
            {
              validator: async (_, value) => {
                if (value != undefined && value.length == 18) {
                  const { data }: any = await apiCheckCoCode(value);
                  if (!data) {
                    setCanGoLogin(true);
                    return Promise.reject('统一社会信用代码已存在！');
                  }
                }
                setCanGoLogin(false);
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input placeholder="请输入" />
        </FormItem>
        <FormItem label="企业所在地（省市区）" name="coProvince">
          <div key="province">
            <Province
              value={() => {
                const data = formData.current[currentStep];
                if (data == undefined) {
                  return [];
                }
                const province = data.coProvince;
                if (province == undefined) {
                  return [];
                }
                return [province.provinceName, province.cityName, province.areaName];
              }}
              width="100%"
              onChange={(e: any) => {
                const provinceData = {
                  provinceName: e?.[0]?.addressName,
                  provinceCode: e?.[0]?.addressId,
                  cityName: e?.[1]?.addressName,
                  cityCode: e?.[1]?.addressId,
                  areaName: e?.[2]?.addressName,
                  areaCode: e?.[2]?.addressId,
                };
                formRef.setFieldValue('coProvince', provinceData);
              }}
            />
          </div>
        </FormItem>
        <FormItem label="详细地址" name="coAddressInfo" rules={[{ max: 100 }]}>
          <Input placeholder="请输入" />
        </FormItem>
        <div className="overlap-date-picker">
          <ProFormDateRangePicker
            label="营业执照有效期"
            name="businessLicenseDate"
            rules={[{ required: require, message: '请输入' }]}
            disabled={!require}
            fieldProps={{ style: { width: '100%' } }}
          />
          <FormItem
            name="licenseExpirationDateType"
            rules={[{ required: true, message: '请输入' }]}
            className="overlap-date-type"
            initialValue={ExpirationDateTypeEnum[1].value}
          >
            <Radio.Group
              options={ExpirationDateTypeEnum}
              onChange={(e) => {
                setRequire(e.target.value == ExpirationDateTypeEnum[1].value);
              }}
            />
          </FormItem>
        </div>
      </Form>
    </>
  );
};

export default CompanyInfoForm;
